// @ts-nocheck
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { colors, fontSizes, layout, fontFamily, shadows } from '@/theme';
import Fetch from '@/utils/Fetch';
import ToastContext from '@/utils/toast-context';
import * as Enums from '@/utils/enums';
import { useRouter } from 'next/router';
import Helper from '@/utils/helper';
import JobCard from '@/components/customer-zone/job/job-card';
import SCInfiniteScroll from '@/components/sc-controls/layout/sc-infinite-scroll';
import PageContext from '@/utils/page-context';
import SCListCardSection from '@/components/sc-controls/layout/sc-list-card-section';
import useMobileView from '@/hooks/useMobileView';
import NoSSR from '@/utils/no-ssr'
import Time from '@/utils/time';
import LeadMessage from '@/components/customer-zone/general/lead-message';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router'

const pageSize = 10;

function JobList() {

  const router = useRouter();
  const { t, c } = router.query; // TODO(tanstack-router): verify router.query → useParams/useSearch

  const toast = useContext(ToastContext);
  const pageContext = useContext(PageContext);
  const [mobileView] = useMobileView();

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t }),
    { enabled: !!t }
  );
  const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0 ? tenantData.Results[0] : null;

  const { data: initialJobsData } = useQuery(
    ['czJobListInit', t, c],
    async () => {
      const [openJobRequest, closedJobRequest, now] = await Promise.all([
        Fetch.post({ url: '/CustomerZone/GetJobs', params: { PageSize: pageSize, SortExpression: "StartDate", SortDirection: "descending", OnlyClosed: false }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
        Fetch.post({ url: '/CustomerZone/GetJobs', params: { PageSize: pageSize, SortExpression: "StartDate", SortDirection: "descending", OnlyClosed: true }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
        Time.nowCZ(t, c, tenant.API)
      ]);
      return { openJobRequest, closedJobRequest, now };
    },
    { enabled: !!tenant }
  );

  const [openJobResults, setOpenJobResults] = useState<any>(null);
  const [openJobTotalResults, setOpenJobTotalResults] = useState(0);
  const [closedJobResults, setClosedJobResults] = useState<any>(null);
  const [closedJobTotalResults, setClosedJobTotalResults] = useState(0);
  const [now, setNow] = useState<any>(null);
  const [openJobsCurrentPage, setOpenJobsCurrentPage] = useState(0);
  const [closedJobsCurrentPage, setClosedJobsCurrentPage] = useState(0);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (tenantData && !tenant) {
      router.replace('/');
    }
  }, [tenantData]);

  useEffect(() => {
    if (initialJobsData && openJobResults === null) {
      setOpenJobResults(initialJobsData.openJobRequest.Results);
      setOpenJobTotalResults(initialJobsData.openJobRequest.TotalResults);
      setClosedJobResults(initialJobsData.closedJobRequest.Results);
      setClosedJobTotalResults(initialJobsData.closedJobRequest.TotalResults);
      setNow(initialJobsData.now);
      (pageContext as any).updateTenantName(initialJobsData.openJobRequest.headerTenantName);
    }
  }, [initialJobsData]);

  useEffect(() => {
    if (openJobsCurrentPage > 0)
      searchJobs(false);
  }, [openJobsCurrentPage]);

  useEffect(() => {
    if (closedJobsCurrentPage > 0)
      searchJobs(true);
  }, [closedJobsCurrentPage]);

  async function searchJobs(onlyClosed: any) {
    setSearching(true);
    const searchRes = await Fetch.post({
      url: '/CustomerZone/GetJobs',
      params: {
        pageIndex: onlyClosed ? closedJobsCurrentPage : openJobsCurrentPage,
        PageSize: pageSize,
        SortExpression: "StartDate",
        SortDirection: "descending",
        OnlyClosed: onlyClosed,
      },
      toastCtx: toast,
      tenantID: t,
      customerID: c,
      apiUrlOverride: tenant.API
    });

    if (onlyClosed) {
      if (closedJobsCurrentPage < 1) {
        setClosedJobResults(searchRes.Results);
        setClosedJobTotalResults(searchRes.TotalResults);
      } else {
        let temp = [...closedJobResults];
        temp.push(...searchRes.Results);
        setClosedJobResults(temp);
        setClosedJobTotalResults(searchRes.TotalResults);
      }
    } else {
      if (openJobsCurrentPage < 1) {
        setOpenJobResults(searchRes.Results);
        setOpenJobTotalResults(searchRes.TotalResults);
      } else {
        let temp = [...openJobResults];
        temp.push(...searchRes.Results);
        setOpenJobResults(temp);
        setOpenJobTotalResults(searchRes.TotalResults);
      }
    }
    setSearching(false);
  }

  function jobClick(job: any) {
    Helper.nextRouter(router.push, `/customerzone/viewjob?t=${t}&c=${c}&i=${job.ID}`);
  }

  if (!tenant || openJobResults === null) return null;

  return (
    <NoSSR>
      <div className={mobileView ? "" : "centered"}
           style={{
             paddingInlineEnd: 'var(--mantine-spacing-sm)',
             minHeight: 'calc(100dvh - 20px)'
           }}
      >

        <LeadMessage customerID={c} tenantID={t} page={"customerzone/joblist"} />

        <h2 className="page-title">Your Jobs</h2>

        <div className={"loading-overlay" + (searching ? " loading-overlay-visible" : "")}>
          <div className="loader"></div>
        </div>

        {openJobResults && openJobResults.length > 0 ? <>
          <SCListCardSection text="Open" colour="Green" />
          {openJobResults.map((job: any, key: any) => {
            return <JobCard key={key} job={job} onClick={(j: any) => jobClick(j)} now={now} />
          })}
          {openJobResults.length < openJobTotalResults ?
            <div className="load-more" onClick={() => setOpenJobsCurrentPage(openJobsCurrentPage + 1)}>Load more</div>
            : ""}
        </> : ""}

        {closedJobResults && closedJobResults.length > 0 ? <>
          <SCListCardSection text="Closed" colour="Blue" />
          {closedJobResults.map((job: any, key: any) => {
            return <JobCard key={key} job={job} onClick={(j: any) => jobClick(j)} now={now} />
          })}
          {closedJobResults.length < closedJobTotalResults ?
            <div className="load-more" onClick={() => setClosedJobsCurrentPage(closedJobsCurrentPage + 1)}>Load more</div>
            : ""}
        </> : ""}

        {(!openJobResults || openJobResults.length === 0) && (!closedJobResults || closedJobResults.length === 0) ? <>
          <h4>There are no jobs</h4>
        </> : ""}

        <style jsx>{`

        .load-more {
          margin: 0.5rem;
          cursor: pointer;
          font-weight: bold;
          text-align: right;
          ${mobileView ? "" : `max-width: ${layout.listCardWidth};`}
        }

        .load-more:hover {
          text-decoration: underline;
        }

        h2.page-title {
          margin: 0;
          font-size: 1.5rem;
        }

        .container {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          padding: 1.5rem 3rem;
          overflow-x: hidden;
        }
        .column {
          width: 100%;
        }
        .column-margin {
          margin-left: 24px;
        }
        .button-container {
          flex-shrink: 0;
          width: 9rem;
        }
        .button-container :global(.button){
          margin-top: 0.5rem;
        }
        .title {
          align-items: center;
          display: flex;
          color: ${colors.darkPrimary};
          width: 100%;
        }
        .title h1 {
          font-size: 24px;
          margin: 6px 0 0 0;
        }
        .title p {
          font-size: 14px;
          margin: 0;
        }
        .row {
          display: flex;
          justify-content: space-between;
        }
        .end {
          align-items: flex-end;
        }
        .padded {
          padding-bottom: 1rem;
        }
        .row.top-gap {
          margin-top: 2.5rem;
        }
        .heading {
          color: ${colors.blueGrey};
          font-size: 14px;
          margin-bottom: 0.5rem;
        }
        .heading-blue {
          color: ${colors.bluePrimary};
        }
        .no-jobs {
          align-items: center;
          box-sizing: border-box;
          display: none;
          flex-direction: column;
          flex-grow: 1;
          height: 100%;
          justify-content: center;
          position: relative;
          text-align: center;
        }
        .no-jobs-visible {
          display: flex;
        }
        .no-jobs h3 {
          color: ${colors.darkPrimary};
          font-size: 18px;
          line-height: 22px;
          margin: 2rem 0 0;
        }
        .no-jobs p {
          color: ${colors.blueGrey};
          margin: 0.5rem 0 0;
        }
        .no-jobs a {
          align-items: center;
          color: ${colors.bluePrimary};
          cursor: pointer;
          display: flex;
          font-weight: bold;
          margin-top: 1rem;
          text-decoration: none;
        }
        .no-jobs a img{
          margin-right: 0.5rem;
        }
        .wave {
          position: absolute;
          bottom: -1.5rem;
          left: -3rem;
          pointer-events: none;
          right: 0;
          width: calc(100% + 6rem);
        }
        .search-container :global(.search) {
          width: 528px;
        }
        .table-container {
          display: block;
          height: 100%;
          overflow: none;
          position: relative;
        }
        .table-container :global(.table) {
          margin-top: 0;
        }
        .table-container-visible {
          display: block;
        }
        .loading-overlay {
          align-items: center;
          /*background-color: rgba(28, 37, 44, 0.6);*/
          background-color: rgba(245, 248, 251, 0.6);
          bottom: 0;
          border-radius: 8px;
          display: none;
          justify-content: center;
          left: -1rem;
          position: absolute;
          right: -1rem;
          top: 0.5rem;
        }
        .loading-overlay-visible {
          display: flex;
        }
        .loading-overlay :global(.loader) {
          border-color: rgba(28, 37, 44, 0.2);
          border-left-color: ${colors.darkPrimary};
          border-width: 0.25rem;
          display: flex;
          height: 1.5rem;
          width: 1.5rem;
        }
        a {
          text-decoration: none;
        }
        .select-actions {
          display: flex;
          margin-left: -1rem;
        }
        .select-actions :global(.white-action) {
          font-size: 0.875rem;
          margin: 0 0 0 1rem; 
          width: auto;
        }
        .select-actions :global(.icon) {
          left: 1rem !important;
        }
        .download {
          margin-right: 1rem;
          width: 3rem;
        }

        .centered {
          position: relative;
          left: 50%;
          transform: translateX(-50%);
          width: ${layout.listCardWidth};
        }
      `}</style>
      </div>
    </NoSSR>
  );
}

export default JobList;

export const Route = createFileRoute('/customerzone/joblist')({ component: JobList })
